import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { ProfileType } from '../../enums/ProfileType';
import { SessionHelper } from '../../helpers/SessionHelper';
import { ProfileHelper } from '../../helpers/ProfileHelper';
import iconGrade from '../../assets/icon-grade.png';
import iconNoGrade from '../../assets/icon-no-grade.png';
import iconHto from '../../assets/icon-hto.png';
import iconReports from '../../assets/icon-reports.png';
import iconShipping from '../../assets/icon-shipping.png';
import iconWebLink from '../../assets/icon-web-link.png';
import iconConfig from '../../assets/icon-config.png';
import iconExpressOrders from '../../assets/icon-express-delivery.png';
import iconSearch from '../../assets/icon-search.png';
import { useAuth } from '../../contexts/AuthContext';
import { Body } from '../../common-components/Layout/Body/Body';
import { Header } from '../../common-components/Layout/Header/Header';
import { Page } from '../../common-components/Layout/Page/Page';
import { HomeMenuItem } from '../../components/HomeMenuItem/HomeMenuItem';
import { FrontVersionDisplay } from '../../components/FrontVersionDisplay/FrontVersionDisplay';
import { HomeMenu } from './styles';
import ServicesContext from '../../contexts/ServicesContext';

interface IMenuItem {
  icon: any | null;
  subtitle: any | null;
  title: any | null;
  click: any | null;
  profiles?: ProfileType[] | null;
};

const Home: React.FC = () => {

  const navigate = useNavigate();
  const { session, signOut } = useAuth();

  const [expressOrdersEnabled, setExpressOrdersEnabled] = React.useState<boolean>(false);

  const {
    featureFlagService,
  } = React.useContext(ServicesContext);

  useEffect(() => {
    if (!session || SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
    }

    featureFlagService.expressOrdersEnabledToProfileType(session?.user?.profileType).then((result) => {
      setExpressOrdersEnabled(result);
    });
  }, [session, navigate]);

  const headerRender = React.useMemo(() =>
    <Header title='Home' alertSummary={ProfileHelper.profilesThatCanManageTransport.some(p => p === session?.user?.profileType)} />
    , [session]);

  const menu = React.useMemo(() => {

    let menuItems: IMenuItem[] = [
      {
        icon: iconNoGrade, subtitle: strings.LogisticsOf, title: strings.OrderNonDegree, click: navigateToOrdersNonDegree,
        profiles: [ProfileType.Admin, ProfileType.Ecommerce, ProfileType.Vendor, ProfileType.Viewer]
      },
      {
        icon: iconGrade, subtitle: strings.LogisticsOf, title: strings.OrderWithDegree, click: navigateToOrdersWithDegree,
        profiles: [ProfileType.Admin, ProfileType.Ecommerce, ProfileType.Vendor, ProfileType.GlobalPicker, ProfileType.Assembler, ProfileType.Viewer]
      },
      {
        icon: iconExpressOrders, subtitle: strings.LogisticsOf, title: strings.ExpressOrders, click: navigateToExpressOrders,
        profiles: expressOrdersEnabled ? [ProfileType.Admin, ProfileType.Ecommerce, ProfileType.Viewer] : []
      },
      {
        icon: iconReports, subtitle: strings.Reports, title: strings.GeneralInThePlural, click: navigateToReports,
        profiles: [ProfileType.Admin, ProfileType.Ecommerce, ProfileType.Vendor, ProfileType.GlobalPicker, ProfileType.Viewer]
      },
      {
        icon: iconShipping, subtitle: strings.Manage, title: strings.Transport, click: navigateToManageTransport,
        profiles: [ProfileType.Admin, ProfileType.Ecommerce, ProfileType.Vendor, ProfileType.Viewer]
      },
      {
        icon: iconWebLink, subtitle: strings.Manage, title: strings.DNPLinks, click: navigateToDnpLinks,
        profiles: [ProfileType.Admin, ProfileType.Ecommerce, ProfileType.Vendor, ProfileType.Viewer]
      },
      {
        icon: iconSearch, subtitle: strings.Manage, title: strings.PendingInvoicingOrders, click: navigateToPendingInvoicingOrders,
        profiles: ProfileHelper.profilesThatCanViewPendingInvoicingOrders
      },
      {
        icon: iconConfig, subtitle: strings.Manage, title: strings.Locations, click: navigateToManageLocations,
        profiles: ProfileHelper.profilesThatCanManageLocations
      },
      {
        icon: iconConfig, subtitle: strings.Settings, title: strings.GeneralInThePlural, click: navigateToAppConfigs,
        profiles: [ProfileType.Admin, ProfileType.Ecommerce, ProfileType.Vendor, ProfileType.Viewer]
      }
    ];

    menuItems = menuItems.filter(menuItem => !menuItem.profiles || menuItem.profiles.some(profile => profile === session?.user?.profileType));

    const homeMenus = [];

    let firstMenuItemIndex = 0;
    let homeMenuItems = menuItems.slice(firstMenuItemIndex, firstMenuItemIndex + 3);
    while (homeMenuItems.length > 0) {
      while (homeMenuItems.length < 3) {
        homeMenuItems.push({ icon: null, subtitle: null, title: null, click: null });
      }

      homeMenus.push((
        <HomeMenu key={`home-menu-${firstMenuItemIndex}`}>
          {homeMenuItems.map((item, xi) => (
            <HomeMenuItem
              key={`home-menu-item-${firstMenuItemIndex}-${xi}`}
              icon={item.icon}
              subtitle={item.subtitle}
              title={item.title}
              click={item.click}
              blank={!item.title}
            />
          ))}
        </HomeMenu>
      ));

      firstMenuItemIndex += 3;
      homeMenuItems = menuItems.slice(firstMenuItemIndex, firstMenuItemIndex + 3);
    }

    return homeMenus;
  }, [session, expressOrdersEnabled]);

  return (
    <Page header={headerRender} footer={<FrontVersionDisplay />}>
      <Body marginTop='3px'>
        {menu}
      </Body>
    </Page>
  );

  function navigateToOrdersWithDegree() {
    navigate(routes.OrdersWithDegree);
  }

  function navigateToOrdersNonDegree() {
    navigate(routes.OrdersNonDegree);
  }

  function navigateToOrdersHomeTryOn() {
    navigate(routes.OrdersHomeTryOn);
  }

  function navigateToReports() {
    navigate(routes.Reports);
  }

  function navigateToManageTransport() {
    navigate(routes.ManageTransport);
  }

  function navigateToDnpLinks() {
    navigate(routes.ManageDnpLinks);
  }

  function navigateToPendingInvoicingOrders() {
    navigate(routes.ManagePendingInvoicingOrders);
  }

  function navigateToManageLocations() {
    navigate(routes.ManageLocations);
  }

  function navigateToAppConfigs() {
    navigate(routes.AppConfigs);
  }

  function navigateToExpressOrders() {
    navigate(routes.ExpressOrders);
  }
}

export default Home;